import { useState } from 'react';
import { X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { WalletConnectDappData } from './types';
import { ConnectDappSection } from './ConnectDappSection';
import { ConnectedDappsSection } from './ConnectedDappsSection';
import { NextStepsSection } from './NextStepsSection';

type WalletConnectSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dappData: WalletConnectDappData[];
  isConnecting: boolean;
  policyGateErrorMessage?: string | null;
  connectToDapp: (uri: string) => Promise<void>;
  cancelPendingConnection: () => void;
  disconnect: (topic: string) => Promise<void>;
};

/**
 * The curtain dialog that opens when clicking walletconnect on the details page
 */
export function WalletConnectSheet({
  open,
  onOpenChange,
  dappData,
  isConnecting,
  policyGateErrorMessage,
  connectToDapp,
  cancelPendingConnection,
  disconnect,
}: WalletConnectSheetProps): React.ReactElement {
  const [walletConnectUri, setWalletConnectUri] = useState('');
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const hasConnections = dappData.length > 0;

  const handleConnect = async () => {
    if (!walletConnectUri.trim()) return;

    setConnectionError(null);
    try {
      await connectToDapp(walletConnectUri.trim());
      setWalletConnectUri('');
    } catch {
      setConnectionError('Unable to connect to dApp');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        overlayClassName="bg-black/25"
        showCloseButton={false}
        className="top-[35px] h-[calc(100dvh-35px)] max-w-none rounded-t-[50px] border-zinc-100 p-0 overflow-hidden"
      >
        <div className="flex h-full flex-col bg-zinc-100">
          <SheetHeader className="relative h-[65px] items-center justify-center border-b border-zinc-200 bg-white px-6">
            <SheetTitle className="text-center text-[20px] font-medium text-black">
              Use WalletConnect to interact with a dapp
            </SheetTitle>
            <button
              type="button"
              aria-label="Close WalletConnect sheet"
              className="absolute right-7 top-2/5 -translate-y-2/5 rounded-[58px] p-[15px] text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-[18px] w-[18px]" />
            </button>
          </SheetHeader>

          <div className="flex flex-1 flex-col items-center overflow-y-auto px-4 py-10">
            <div className="flex w-full max-w-[479px] flex-col gap-[12px]">
              {/* Case: at least one dapp connected, show the connected dapps section */}
              {hasConnections && (
                <ConnectedDappsSection
                  dappData={dappData}
                  onDisconnect={disconnect}
                />
              )}
              <ConnectDappSection
                walletConnectUri={walletConnectUri}
                onWalletConnectUriChange={setWalletConnectUri}
                isConnecting={isConnecting}
                hasConnections={hasConnections}
                onConnect={handleConnect}
                onPendingDappConnectionCancelled={() => {
                  setWalletConnectUri('');
                  setConnectionError(null);
                  cancelPendingConnection();
                }}
                connectionError={connectionError}
              />
              <NextStepsSection />
              {policyGateErrorMessage && (
                <p className="text-sm text-destructive">
                  {policyGateErrorMessage}
                </p>
              )}
            </div>
          </div>

          <div className="flex h-[74px] items-center justify-center border-t border-zinc-200 bg-white p-[10px]">
            <Button
              type="button"
              variant="outline"
              className="h-[37px] rounded-[9px] border-zinc-200 px-[19px] py-[6px] text-[14px] font-semibold text-zinc-900 hover:bg-zinc-50"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
